import { useCallback, useEffect, useState } from 'react'
import classNames from 'classnames'
import { Breakpoint, useBreakpoint } from '../breakpoints'
import { DownloadIcon } from '../icons/downloadIcon'
import { useProject } from '../project/hook'
import { downloadProjectRbxl } from '../project/api'
import { useTranslator } from '../translator/hook'
import { Tooltip } from '../tooltip'
import { useTooltip } from '../tooltipHook'
import { LoadingBox } from '../loading/loadingBox'
import './button.css'
import { DownloadModal } from '../modals/help/downloadModal'
import { ErrorModal } from '../modals/errorModal'

const DOWNLOAD_BUTTON_COOLDOWN = 5000

interface DownloadButtonProps {
  useDownloadModal?: boolean
  onDownloadClick?: () => void
  onDownloadFailed?: () => void
}

export const DownloadButton = ({
  useDownloadModal = true,
  onDownloadClick,
  onDownloadFailed,
}: DownloadButtonProps) => {
  const { t } = useTranslator()
  const { canEdit, missionManager, projectId, saveProject } = useProject()
  const isWide = !useBreakpoint(canEdit ? Breakpoint.phase1 : Breakpoint.phase2)
  const [isDisabled, setIsDisabled] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const [downloadError, setDownloadError] = useState<string | null>(null)
  const { getReferenceProps, refs, tooltipProps } = useTooltip()

  useEffect(() => {
    if (!isDisabled) return
    const timeout = setTimeout(
      () => setIsDisabled(false),
      DOWNLOAD_BUTTON_COOLDOWN,
    )
    return () => clearTimeout(timeout)
  }, [isDisabled])

  const downloadTemplate = useCallback(async () => {
    setIsDisabled(true)

    // 原本 rbxl に Blockly 編集（保存済み code）をサーバで逐次マージして取得する。
    // マージはサーバ処理で時間がかかり得るため共通ローディングを表示する。
    setIsLoading(true)
    setDownloadError(null)
    try {
      // サーバは S3 の code.json を読むため、編集中のデバウンス保存を先に確定させ、
      // 最新のコードでマージされるようにする（編集権がある場合のみ）。
      // 保存に失敗したら古いコードでDLしないよう中止する。
      if (canEdit) {
        const saved = await saveProject()
        if (!saved) {
          throw new Error(
            'プロジェクトの保存に失敗したためダウンロードを中止しました',
          )
        }
      }
      await downloadProjectRbxl(
        projectId,
        missionManager.template,
        `${projectId}.rbxl`,
      )
      // DL が成立した後にだけチュートリアル完了などのコールバックを呼ぶ
      // （失敗時に完了扱いにしない）。
      onDownloadClick?.()
      setIsOpen(true)
    } catch (error) {
      console.error('rbxl のダウンロードに失敗しました', error)
      // ダウンロード導線（z-index の高いオーバーレイ）の下にエラーが隠れないよう、
      // 先に呼び出し側へ失敗を伝えてからエラーを提示する。
      onDownloadFailed?.()
      setDownloadError(t('download_failed_detail'))
    } finally {
      setIsLoading(false)
    }
  }, [
    canEdit,
    saveProject,
    missionManager,
    projectId,
    onDownloadClick,
    onDownloadFailed,
    t,
  ])

  return (
    <>
      <LoadingBox loading={isLoading} />
      <div
        className={classNames('download-small-button', {
          'disabled-small-button': isDisabled || isLoading,
        })}
        aria-disabled={isDisabled || isLoading}
        onClick={isDisabled || isLoading ? undefined : downloadTemplate}
        ref={refs.setReference}
        {...getReferenceProps()}
      >
        <DownloadIcon style={{ verticalAlign: 'middle' }} />
        {isWide && <span style={{ marginLeft: '5px' }}>{t('download')}</span>}
      </div>
      <Tooltip
        {...tooltipProps}
        className="download-button-tooltip"
        disabled={isWide}
      >
        {t('download')}
      </Tooltip>
      {useDownloadModal && (
        <DownloadModal isOpen={isOpen} onClose={() => setIsOpen(false)} />
      )}
      <ErrorModal
        error={downloadError}
        modalTitle={t('download_failed_title')}
        onClose={() => setDownloadError(null)}
      />
    </>
  )
}
